export const FONT = '"Segoe UI", Arial, Helvetica, sans-serif';

export const COLORS = {
  primary: '#3398d3',
  primaryDark: '#1f6f9f',
  pageBg: '#eef2f6',
  cardBg: '#ffffff',
  softBg: '#f8fafc',
  border: '#e2e8f0',
  title: '#0f172a',
  text: '#334155',
  muted: '#64748b',
  barTrack: '#e6eef5',
  badgeBg: '#e8f3fb',
  green: '#27ae60',
  red: '#ef4444'
};

export const WIDTH = 1080;
export const TARGET_H = 1920; // proporção de celular (9:16)
export const PAD = 48;
export const HEADER_H = 380;
export const FOOTER_H = 60;
export const BADGE_W = 112;
export const ROW_MIN_H = 64;
export const ROW_MAX_H = 110;

const LOGO_W = 580;
const LOGO_RATIO = 2560 / 1468;

export const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

export const fill = (ctx: CanvasRenderingContext2D, color: string) => {
  ctx.fillStyle = color;
  ctx.fill();
};

export const strokeBorder = (ctx: CanvasRenderingContext2D, color: string) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.stroke();
};

export const setFont = (ctx: CanvasRenderingContext2D, weight: number, size: number) => {
  ctx.font = `${weight} ${size}px ${FONT}`;
};

/** Reduz o corpo da fonte até caber; se ainda estourar, corta com reticências. */
export const fitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  weight: number,
  maxSize: number,
  minSize: number
) => {
  for (let size = maxSize; size >= minSize; size -= 1) {
    setFont(ctx, weight, size);
    if (ctx.measureText(text).width <= maxWidth) return text;
  }
  setFont(ctx, weight, minSize);
  let recortado = text;
  while (recortado.length > 1 && ctx.measureText(`${recortado}…`).width > maxWidth) {
    recortado = recortado.slice(0, -1);
  }
  return `${recortado}…`;
};

export const formatDate = () =>
  new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date());

export const loadLogo = () =>
  new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = '/logo.png';
  });

/** Distribui a altura das linhas para aproximar a proporção de celular. */
export const calcRowHeight = (linhas: number, alturaFixa: number) => {
  if (linhas <= 0) return ROW_MIN_H;
  const disponivel = TARGET_H - alturaFixa;
  return Math.max(ROW_MIN_H, Math.min(ROW_MAX_H, Math.floor(disponivel / linhas)));
};

export const createCanvas = (height: number) => {
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(scale, scale);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.pageBg;
  ctx.fillRect(0, 0, WIDTH, height);
  return { canvas, ctx };
};

export const drawHeader = (
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  titulo: string,
  subtitulo: string
) => {
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEADER_H);
  gradient.addColorStop(0, COLORS.primary);
  gradient.addColorStop(1, COLORS.primaryDark);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, WIDTH, HEADER_H);

  ctx.textAlign = 'center';
  if (logo) {
    ctx.drawImage(logo, (WIDTH - LOGO_W) / 2, 8, LOGO_W, LOGO_W / LOGO_RATIO);
  }
  ctx.fillStyle = '#ffffff';
  const tituloAjustado = fitText(ctx, titulo, WIDTH - PAD * 2, 800, 38, 24);
  ctx.fillText(tituloAjustado, WIDTH / 2, 326);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  setFont(ctx, 600, 18);
  ctx.fillText(subtitulo, WIDTH / 2, 358);
  ctx.textAlign = 'left';
};

export const drawFooter = (ctx: CanvasRenderingContext2D, y: number, resumo: string) => {
  ctx.fillStyle = COLORS.muted;
  setFont(ctx, 600, 13);
  ctx.fillText(`Gerado em ${formatDate()}`, PAD, y + 22);
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.text;
  setFont(ctx, 700, 13);
  ctx.fillText(resumo, WIDTH - PAD, y + 22);
  ctx.textAlign = 'left';
};

export const downloadCanvas = (canvas: HTMLCanvasElement, filename: string) => {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 'image/png');
};
